import { logToConsole } from '@/utils/logging';
import { getConsentCategoryPreferences, getConsentCategoryState, type ConsentCategoryPreferences } from './uc-consent';

export interface SegmentConsentTarget {
  load: () => Promise<void>;
  suspend: () => void;
  hasStarted: () => boolean;
}

interface TargetState {
  pending: Promise<boolean> | null;
  loaded: boolean;
  failed: boolean;
}

export interface SegmentConsent {
  readonly categoryPreferences: ConsentCategoryPreferences;
}

export async function getSegmentConsent(): Promise<SegmentConsent> {
  return {
    categoryPreferences: await getConsentCategoryPreferences()
  };
}

/** Owns the Functional consent lifecycle for every Segment source on a page. */
export class SegmentConsentCoordinator {
  private readonly targets = new Map<SegmentConsentTarget, TargetState>();
  private initialConsent: Promise<void> | null = null;
  private allowed = false;
  private revision = 0;
  private revocations = 0;
  private reloadRequested = false;

  get isAllowed(): boolean {
    return this.allowed;
  }

  async initialize(target: SegmentConsentTarget): Promise<boolean> {
    if (this.targets.has(target) === false) {
      this.targets.set(target, {
        pending: null,
        loaded: false,
        failed: false
      });
    }

    if (this.initialConsent === null) {
      window.addEventListener('ucConsentChangedEvent', this.handleConsentChange);

      this.initialConsent = this.readInitialConsent(this.revision);
    }

    await this.initialConsent;

    return this.start(target);
  }

  dispose(): void {
    window.removeEventListener('ucConsentChangedEvent', this.handleConsentChange);
    this.allowed = false;
    this.revision += 1;

    for (const target of this.targets.keys()) {
      target.suspend();
    }

    this.targets.clear();
  }

  private async readInitialConsent(initialRevision: number): Promise<void> {
    try {
      const allowed = await getConsentCategoryState('Functional');

      if (this.revision === initialRevision) {
        this.allowed = allowed;
      }
    } catch {
      // Consent lookup failure keeps delivery blocked; a later consent event can grant it.
    }
  }

  private async start(target: SegmentConsentTarget): Promise<boolean> {
    const state = this.targets.get(target);

    if (state === undefined || this.allowed === false || state.failed) {
      return false;
    }

    if (state.pending !== null) {
      return state.pending;
    }

    if (state.loaded) {
      return true;
    }

    state.pending = this.loadTarget(target, state, this.revocations);

    return state.pending;
  }

  private async loadTarget(
    target: SegmentConsentTarget, state: TargetState, loadRevocations: number
  ): Promise<boolean> {
    // Defer startup until start() has stored the shared pending promise.
    await Promise.resolve();

    try {
      if (this.allowed === false || this.revocations !== loadRevocations) {
        return false;
      }

      await target.load();
      state.loaded = this.allowed && this.revocations === loadRevocations;

      return state.loaded;
    } catch (error: unknown) {
      state.failed = true;
      logToConsole('Failed to load analytics instance:', error);

      return false;
    } finally {
      state.pending = null;

      if (this.allowed && state.loaded === false && state.failed === false) {
        void this.start(target);
      }
    }
  }

  private readonly handleConsentChange = (event: Event): void => {
    if (event instanceof CustomEvent === false) {
      return;
    }

    const detail: unknown = event.detail;

    if (typeof detail !== 'object' || detail === null || 'ucCategory' in detail === false) {
      return;
    }

    const { ucCategory } = detail;

    if (typeof ucCategory !== 'object' || ucCategory === null ||
      'functional' in ucCategory === false || typeof ucCategory.functional !== 'boolean') {
      return;
    }

    const allowed = ucCategory.functional;

    this.revision += 1;
    this.allowed = allowed;

    if (allowed) {
      for (const target of this.targets.keys()) {
        void this.start(target);
      }

      return;
    }

    let hasStarted = false;

    this.revocations += 1;

    for (const [target, state] of this.targets) {
      hasStarted = target.hasStarted() || hasStarted;
      state.loaded = false;
      target.suspend();
    }

    if (hasStarted && this.reloadRequested === false) {
      this.reloadRequested = true;
      window.location.reload();
    }
  };
}
